import { atom } from 'nanostores';
import { useEffect } from 'react';

/**
 * Whether the generation wizard currently owns the page with a full-page
 * takeover (authoring progress, the error cards, the document review).
 *
 * The run's banner sits above the masthead, outside the wizard's subtree, and
 * follows the same tracked run — so while a takeover is on screen the banner
 * would repeat what the takeover already says (and offer a primary that just
 * re-enters the state the user is already in). The wizard publishes its
 * presence here and `BrandRunPageBanner` stands down.
 *
 * An atom rather than a prop or context because the two live in different
 * subtrees: the page renders the banner above its own header, the wizard mounts
 * inside the page body.
 */
export const $brandWizardTakeover = atom(false);

export function setBrandWizardTakeover(isTakeover: boolean): void {
  $brandWizardTakeover.set(isTakeover);
}

/**
 * Publish the wizard's takeover presence for as long as one is up. The cleanup
 * lowers the flag, so a phase change AND an unmount (navigating away mid-run)
 * both clear it — the banner must never be left hidden by a wizard that is gone.
 */
export function usePublishBrandWizardTakeover(isTakeover: boolean): void {
  useEffect(
    function publishTakeoverPresence() {
      if (!isTakeover) return;
      setBrandWizardTakeover(true);
      return () => setBrandWizardTakeover(false);
    },
    [isTakeover],
  );
}
